import { ArrowDownIcon, ArrowUpIcon, Check, EyeIcon } from "lucide-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { SectionListItem } from "@/client";
import { ErrorElement } from "@/components/error-element";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { Button } from "@/components/ui/button";
import { useGetSections } from "@/features/abstracts-sections/api/get-sections";
import { DataTable } from "@/features/abstracts-sections/components/data-table";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { pageTitle } from "@/utils/page-title";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

export function SectionListPage() {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<SectionListItem>();
  const { pagination, setPagination, search, setSearch } = useTableControl();
  const [sorting, setSorting] = useState<SortingState>([]);
  const handleSortingChange: Dispatch<SetStateAction<SortingState>> = (
    updater,
  ) => {
    setSorting(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };
  const sortBy =
    sorting[0]?.id === "rating" || sorting[0]?.id === "ratingCount"
      ? (sorting[0].id as "rating" | "ratingCount")
      : undefined;
  const sortOrder =
    sortBy !== undefined
      ? sorting[0].desc
        ? "desc"
        : "asc"
      : undefined;
  const { congress } = useCongress();
  const {
    data: userData,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSuccess,
  } = useGetSections({
    congressId: congress.id,
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    search: search ?? undefined,
    sortBy,
    sortOrder,
  });
  const columns = useMemo(
    () => [
      columnHelper.accessor("shortName", {
        id: "shortName",
        cell: (info) => {
          const shortName = info.getValue();
          if (!shortName) return "-";
          return shortName;
        },
        header: () => <span>{t("table.shortName")}</span>,
        enableSorting: false,
        size: 110,
      }),
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => {
          const name = info.getValue();
          if (!name) return "-";
          return name;
        },
        header: () => <span>{t("table.name")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("poster", {
        id: "poster",
        cell: (info) => {
          const poster = info.getValue();
          if (!poster) return "-";
          return <Check className="size-4" strokeWidth={2.5} />;
        },
        header: () => <span>{t("table.poster")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("startTime", {
        id: "startTime",
        cell: (info) => {
          const startTime = info.getValue();
          if (!startTime) return "-";
          return startTime;
        },
        header: () => <span>{t("table.startTime")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("endTime", {
        id: "endTime",
        cell: (info) => {
          const endTime = info.getValue();
          if (!endTime) return "-";
          return endTime;
        },
        header: () => <span>{t("table.endTime")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("date", {
        id: "date",
        cell: (info) => {
          const from = info.getValue();
          if (!from) {
            return "-";
          }

          return from;
        },
        header: () => <span>{t("table.from")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("dateEnd", {
        id: "dateEnd",
        cell: (info) => {
          const to = info.getValue();
          if (!to) {
            return "-";
          }

          return to;
        },
        header: () => <span>{t("table.to")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("rating", {
        id: "rating",
        cell: (info) => {
          const rating = info.getValue();
          if (rating == null) return "–";
          return String(rating);
        },
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <span>{t("table.rating")}</span>
            {column.getIsSorted() === "asc" && (
              <ArrowUpIcon className="ml-1 size-4" />
            )}
            {column.getIsSorted() === "desc" && (
              <ArrowDownIcon className="ml-1 size-4" />
            )}
          </Button>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor("ratingCount", {
        id: "ratingCount",
        cell: (info) => {
          const ratingCount = info.getValue();
          if (ratingCount == null) return "0";
          return String(ratingCount);
        },
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <span>{t("table.ratings")}</span>
            {column.getIsSorted() === "asc" && (
              <ArrowUpIcon className="ml-1 size-4" />
            )}
            {column.getIsSorted() === "desc" && (
              <ArrowDownIcon className="ml-1 size-4" />
            )}
          </Button>
        ),
        enableSorting: true,
      }),
      columnHelper.display({
        id: "view",
        cell: (info) => {
          return (
            <Button variant="ghost" size="icon" asChild>
              <Link to={`/sections/${info.row.original.id}`}>
                <span className="sr-only">{t("table.view")}</span>
                <EyeIcon className="size-4" />
              </Link>
            </Button>
          );
        },
        header: () => <span>{t("table.action")}</span>,
        enableSorting: false,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper, t],
  );

  if (isLoading) return <LoadingScreen />;

  if (!isSuccess) {
    return <ErrorElement />;
  }

  return (
    <div>
      <title>{pageTitle(t("meta.sections"))}</title>
      <SearchBar search={search} setSearch={setSearch} className="mb-4" />
      <DataTable
        columns={columns}
        data={userData.items}
        pagination={pagination}
        pageCount={Math.ceil(userData.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    </div>
  );
}
